import gsap from 'gsap';
import TextPlugin from 'gsap/TextPlugin';

gsap.registerPlugin(TextPlugin);

const clamp = (value: number) => Math.min(1, Math.max(0, value));

export default function initAnimations(
    wrapper: HTMLElement,
    container: HTMLElement, // section，height = 100svh（稳定，不随地址栏变化）
    inputContainer: HTMLElement,
    inputText: string,
    button: HTMLElement,
    batchImageContainer: HTMLDivElement,
    video: HTMLVideoElement,
    videoLoading: HTMLDivElement,
) {
    button.classList.add('disabled');
    gsap.set(inputContainer, { borderColor: '#333333' });
    gsap.set(batchImageContainer, { scale: 0.1, opacity: 0 });
    gsap.set(video, { opacity: 0 });
    gsap.set(videoLoading, { opacity: 0 });

    // ★ wrapper = 150svh，section = 100svh，sticky 实际滚动区间 = 50svh
    // container.clientHeight = 100svh，乘以 0.5 得到 50svh 的像素值
    const animationRange = container.clientHeight * 0.7;
    const inputTextElement = inputContainer.querySelector<HTMLElement>(
        '#three-d-generate-input-text',
    );

    let videoPlayed = false;
    let videoReady = video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;

    const requestVideoPlayback = () => {
        video.play().catch(() => {
            updateVideoVisibility();
        });
    };

    const updateVideoVisibility = () => {
        if (!videoPlayed) {
            video.style.opacity = '0';
            videoLoading.style.opacity = '0';
            return;
        }

        video.style.opacity = videoReady ? '1' : '0';
        videoLoading.style.opacity = videoReady ? '0' : '1';
    };

    const handleVideoReady = () => {
        videoReady = true;
        updateVideoVisibility();

        if (videoPlayed && video.paused) {
            requestVideoPlayback();
        }
    };

    const handleVideoWaiting = () => {
        if (!videoPlayed) {
            return;
        }

        videoReady = false;
        updateVideoVisibility();
    };

    video.addEventListener('loadeddata', handleVideoReady);
    video.addEventListener('canplay', handleVideoReady);
    video.addEventListener('playing', handleVideoReady);
    video.addEventListener('waiting', handleVideoWaiting);
    video.addEventListener('stalled', handleVideoWaiting);

    const updateMainProgress = (p: number) => {
        if (p >= 0.66) {
            if (!videoPlayed) {
                videoPlayed = true;
                videoReady =
                    video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
                updateVideoVisibility();
                video.currentTime = 0;
                requestVideoPlayback();
            } else if (videoReady && video.paused) {
                requestVideoPlayback();
            }
        } else if (videoPlayed) {
            videoPlayed = false;
            updateVideoVisibility();
            video.pause();
        }

        if (p > 0.04) {
            button.classList.remove('disabled');
        } else {
            button.classList.add('disabled');
        }

        if (p < 0.36) {
            document.documentElement.style.setProperty(
                '--three-d-generate-input-opacity',
                '1',
            );
        } else if (p >= 0.36 && p <= 0.66) {
            const opacity = 1 - (p - 0.36) / 0.3;
            document.documentElement.style.setProperty(
                '--three-d-generate-input-opacity',
                `${opacity}`,
            );
        } else {
            document.documentElement.style.setProperty(
                '--three-d-generate-input-opacity',
                '0',
            );
        }
    };

    const tl = gsap.timeline({ paused: true });
    tl.eventCallback('onUpdate', () => updateMainProgress(tl.progress()));

    tl.to({}, { duration: 100 });
    tl.to(inputContainer, { borderColor: 'transparent', duration: 2 }, 0);
    if (inputTextElement) {
        tl.to(
            inputTextElement,
            {
                text: { value: inputText, rtl: false },
                opacity: 1,
                duration: 30,
            },
            4,
        );
    }
    tl.to(inputContainer, { scale: 0, ease: 'none', duration: 30 }, 36);
    tl.to(
        batchImageContainer,
        { scale: 1, opacity: 1, ease: 'none', duration: 30 },
        36,
    );

    let frame: number | null = null;

    const updateFromScroll = () => {
        frame = null;
        const wrapperTop = wrapper.getBoundingClientRect().top;

        if (wrapperTop > 0) {
            tl.progress(0, true);
            document.documentElement.style.setProperty(
                '--three-d-generate-input-opacity',
                '1',
            );
            return;
        }

        const mainProgress = clamp(-wrapperTop / Math.max(animationRange, 1));
        tl.progress(mainProgress);
    };

    const scheduleUpdate = () => {
        if (frame !== null) return;
        frame = requestAnimationFrame(updateFromScroll);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    let prevWidth = window.innerWidth;
    const handleResize = () => {
        const widthChanged = window.innerWidth !== prevWidth;
        const isTouchViewport = navigator.maxTouchPoints > 0;

        if (isTouchViewport && !widthChanged) {
            return;
        }

        prevWidth = window.innerWidth;
        scheduleUpdate();
    };

    window.addEventListener('resize', handleResize);
    updateFromScroll();

    return () => {
        if (frame !== null) {
            cancelAnimationFrame(frame);
        }
        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', handleResize);
        video.removeEventListener('loadeddata', handleVideoReady);
        video.removeEventListener('canplay', handleVideoReady);
        video.removeEventListener('playing', handleVideoReady);
        video.removeEventListener('waiting', handleVideoWaiting);
        video.removeEventListener('stalled', handleVideoWaiting);
        tl.kill();
        video.pause();
    };
}
